import React from 'react'
import { Box, Typography, styled } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { MainButton } from 'src/components/MainButton'
import { MUIThemeColors } from 'src/mui-theme'
import { GetRGBA, getThemeColor } from 'src/utils'
import { formatVersionLabel } from './projectVersion.action'
import { RevisionConfigure } from './useRevisionConfigure'

interface RevisionDialogFooterProps {
  revision: RevisionConfigure
  onClose: () => void
}

// Bottom bar of Configure Positions in a draft: which revision is open and how much it changes.
export const RevisionDialogFooter: React.FC<RevisionDialogFooterProps> = ({ revision, onClose }) => {
  const { t: translate } = useTranslation(['projects', 'common'])
  const count = revision.changes?.positions.length ?? 0

  return (
    <Footer>
      <Tag>
        {translate('revision-draft-tag', {
          ns: 'projects',
          version: formatVersionLabel(revision.project.version?.versionNumber ?? 0),
        })}
      </Tag>
      <Typography variant="body2" color="text.secondary" sx={{ marginRight: 'auto' }}>
        {translate('revision-changed-positions', { ns: 'projects', count })}
      </Typography>
      <Box>
        <MainButton onClick={onClose}>{translate('close', { ns: 'common' })}</MainButton>
      </Box>
    </Footer>
  )
}

export default RevisionDialogFooter

const Footer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: theme.spacing(1),
  marginTop: theme.spacing(1),
  paddingTop: theme.spacing(1.5),
  borderTop: `1px solid ${getThemeColor(theme, MUIThemeColors.secondaryLight)}`,
}))

const Tag = styled('span')(({ theme }) => {
  const accent = getThemeColor(theme, MUIThemeColors.primaryMain)
  return {
    padding: theme.spacing(0.25, 1),
    borderRadius: 4,
    border: `1px solid ${GetRGBA(accent, 0.25)}`,
    backgroundColor: GetRGBA(accent, 0.05),
    color: accent,
    fontSize: 11.5,
    fontWeight: 700,
  }
})
